/** @jsxImportSource @emotion/react */
import {
  Input,
  Button,
  Typography,
  createStyles,
} from '@quickbit/qb-design-system';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../../../context/Session';
import { usePasswordReset } from '../hooks/usePasswordReset';
import { PasswordResetModal } from './PasswordResetModal';

export const PasswordReset = () => {
  const style = useStyle();
  const navigate = useNavigate();
  const { authenticated } = useSession();
  const [email, setEmail] = useState('');
  const { passwordReset, invalidEmail, loading } = usePasswordReset(email);
  const [feedbackModal, setFeedbackModal] = useState<{
    isOpen: boolean;
    status: 'error' | 'success';
  }>({
    isOpen: false,
    status: 'error',
  });

  useEffect(() => {
    if (authenticated) {
      void navigate(-1); // Go back!
    }
  }, [authenticated, navigate]);

  const handlePasswordReset = async () => {
    const response = await passwordReset();

    if (!loading && !response.errorCode) {
      setFeedbackModal({ isOpen: true, status: 'success' });
    }

    if (!loading && response.errorCode) {
      setFeedbackModal({ isOpen: true, status: 'error' });
    }
  };

  return (
    <section css={style.container}>
      <div css={style.card}>
        <div css={style.header}>
          <Typography variant="subtitle">Forgot password</Typography>
          <Typography variant="bodySmall">
            Please enter the email address associated with your account.
          </Typography>
        </div>
        <div css={style.inputField}>
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            label="Email"
            error={email.length !== 0 && invalidEmail}
            helperText={
              email.length !== 0 && invalidEmail
                ? 'Please enter a valid email'
                : ''
            }
          />
        </div>
        <div css={style.actions}>
          <Button
            disabled={invalidEmail || loading}
            variant="primary"
            text="Send reset email"
            onClick={handlePasswordReset}
          />
          <Button
            disabled={loading}
            variant="text"
            text="Cancel"
            onClick={() => navigate('/login', { replace: true })}
          />
        </div>
      </div>
      {!loading && (
        <PasswordResetModal
          isOpen={feedbackModal.isOpen}
          status={feedbackModal.status}
          buttonOnClick={
            feedbackModal.status === 'error'
              ? () => {
                  setFeedbackModal({ ...feedbackModal, isOpen: false });
                }
              : () => {
                  setFeedbackModal({ ...feedbackModal, isOpen: false });
                  void navigate('/login', { replace: true });
                }
          }
        />
      )}
    </section>
  );
};

const useStyle = createStyles(({ spacing, palette, shape, sizing }) => ({
  container: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    borderRadius: shape.borderRadius,
    border: `1px solid ${palette.grey.x100}`,
    background: palette.white,
    padding: `${spacing.xs} ${spacing.xxs}`,
    width: 'fit-content',
    minWidth: sizing.small,
  },
  header: {
    marginBottom: spacing.xs,
    '& > p:first-of-type': {
      marginBottom: spacing.xxxs,
    },
  },
  inputField: {
    display: 'flex',
    marginTop: spacing.s,
    marginBottom: spacing.xs,
  },

  actions: {
    '* + * ': {
      marginTop: spacing.xxxs,
    },
  },
}));
